import React, { useEffect, useState, useRef, useContext } from "react";
import { Container, Button, Row, Col, Spinner } from "reactstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import CommonForm from "../../../../Components/Common/CommonForm";
import SectionCard from "../../../../Components/Common/SectionCard";
import FilePreviewModal from "../../../../Components/Common/FilePreviewModal";
import FullPageLoader from "../../../../Components/Common/FullPageLoader";
import formStyles from "../../../../assets/scss/common/formStyle.module.scss";
import styles from "../css/addTransferVolunteer.module.scss";
import addVolunteerStyle from "../../MasterVolunteer/css/volunteer.module.scss";
import {
  getSubjectFields,
  getTransferFields,
  getReasonFields,
  getEligibilityFields,
} from "../helper/transfervolunteer.constant";
import { TransferVolunteerFormValues } from "../helper/transfervolunteer.types";
import { TRANSFER_VOLUNTEER_PATHS, STUDY_PATHS } from "Routes/paths";
import { useVolunteerService } from "modules/Volunteer/MasterVolunteer/api/useVolunteerQuery";
import { useMasterStudyService } from "modules/Clinical/StudyDetails/api/useStudyQuery";
import { showToastMessage } from "common/service/CommonService";
import { BreadcrumbContext } from "Layouts/Header";
import { queryClient } from "api/queryClient";
import Reason from "common/utilities/Reason/Reason";
import { VOLUNTEER_TRANSFER_TYPE_OPTIONS, VOLUNTEER_TRANSFER_REASON_OPTIONS, loadCodelistsDetailId } from "api/modules/common.option";
import { useTransferVolunteerService } from "../api/useTransferVolunteerQuery";
import TruncatedText from "Components/Common/TruncatedText";
import CommonFileProgress from "../../../../Components/Common/CommonFileUpload/CommonFileProgress";
import { useGlobalStudy } from "context/StudyContext";

const parseSizeToBytes = (sizeStr: any): number => {
  if (!sizeStr) return 0;
  if (typeof sizeStr === "number") return sizeStr;

  const str = String(sizeStr).toLowerCase().trim();
  const val = parseFloat(str);
  if (isNaN(val)) return 0;

  if (str.includes("gb")) return val * 1024 * 1024 * 1024;
  if (str.includes("mb")) return val * 1024 * 1024;
  if (str.includes("kb")) return val * 1024;
  return val;
};


const AddTransferVolunteer: React.FC = () => {
  document.title = "Add Transfer Volunteer | ZENTIXS VISTA";
  
  // -------------------------------------------------------------------------------------------
  // 1. Hooks & Context
  // -------------------------------------------------------------------------------------------
  const navigate = useNavigate();
  const { id: editId } = useParams<{ id: string }>();
  const breadcrumbContext = useContext(BreadcrumbContext);
  const { selectedStudy: globalStudy } = useGlobalStudy();

  const selectedstudyid = Number(globalStudy?.studyid);
  const selectedstudycode = globalStudy?.studyname;

  // Local Storage Variables
  const selectedvolunteerid = Number(sessionStorage.getItem("activevolunteerid"));
  const location = useLocation();

  // -------------------------------------------------------------------------------------------
  // 2. Queries & Mutations
  // -------------------------------------------------------------------------------------------
  const { SelectQuery: TransferSelectQuery, InsertMutation, UpdateMutation } = useTransferVolunteerService({
    volunteertransferid: editId ? Number(editId) : null,
  });
  const transferRecord = TransferSelectQuery?.data?.data?.[0];

  const volunteerIdToFetch = editId && transferRecord ? Number(transferRecord.volunteerid) : selectedvolunteerid;
  const { SelectQuery: VolunteerSelectQuery } = useVolunteerService({
    id: volunteerIdToFetch,
  });
  const volunteerDetails = VolunteerSelectQuery?.data?.data[0];
  const profileurl = sessionStorage.getItem("filebaseUrl") + "/" + volunteerDetails?.profileimg;

  // Destination Study options query
  // const { DropDownAllQuery: studySelectQuery } = useMasterStudyService({
  //   enabledDropDown: true,
  // });
  const {StudyDropdownQuery: studySelectQuery} = useTransferVolunteerService({
      getDropdown: true,
      studyid : Number(globalStudy?.studyid),
      volunteerid: volunteerIdToFetch || selectedvolunteerid,

  })
  const studyOptions = (studySelectQuery?.data as any)?.data
    ?.filter((s: any) => s.studyid !== selectedstudyid) // Exclude current study from target options
    ?.map((s: any) => ({
      label: s.studycode,
      value: s.studyid,
    })) || [];

  // -------------------------------------------------------------------------------------------
  // 3. Form Setup
  // -------------------------------------------------------------------------------------------
  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<TransferVolunteerFormValues>({
    defaultValues: {
      studycode: selectedstudycode,
      volunteerinitial: volunteerDetails?.initials || "",
      transfertype: "",
      targetstudyid: "",
      rescreeningrequired: "No",
      transferreason: "",
      remarks: "",
    } as any,
    mode: "onBlur",
  });

  // -------------------------------------------------------------------------------------------
  // 4. States
  // -------------------------------------------------------------------------------------------
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<any[]>([]);
  const [deletedAttachmentIds, setDeletedAttachmentIds] = useState<number[]>([]);
  const [isReasonModal, setIsReasonModal] = useState<boolean>(false);
  const [pendingUpdatePayload, setPendingUpdatePayload] = useState<any | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [previewFileUrl, setPreviewFileUrl] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  // -------------------------------------------------------------------------------------------
  // 5. Effects
  // -------------------------------------------------------------------------------------------
  useEffect(() => {
    document.title = `${editId ? "Edit" : "Add"} Transfer Record | ZENTIXS ORBIT`;
  }, [editId]);

  useEffect(() => {
    editId ? (breadcrumbContext?.setBreadcrumbItems?.([
      { text: "Transfer Volunteer", link: TRANSFER_VOLUNTEER_PATHS.LIST },
      { text: "Edit Transfer Record" },
    ])) : (breadcrumbContext?.setBreadcrumbItems?.([
      { text: "Screening List", link: STUDY_PATHS.SCREENING_LIST },
      { text: "Add Transfer Record" },
    ]));

    return () => breadcrumbContext?.setBreadcrumbItems?.([]);
  }, [editId]);

  useEffect(() => {
    if (editId && transferRecord) {
      setExistingAttachments(transferRecord.attachments || []);
      reset({
        studycode: transferRecord.fromstudycode || transferRecord.studycode || "",
        volunteerinitial: transferRecord.initials,
        transfertype: transferRecord.transfertype,
        targetstudyid: transferRecord.tostudyid || "",
        rescreeningrequired: transferRecord.isscreeningrequiredtext || (transferRecord.isscreeningrequired ? "Yes" : "No"),
        transferreason: transferRecord.transferreason || "",
        remarks: transferRecord.remarks || "",
      } as any);
    } else {
      reset({
        studycode: selectedstudycode || "",
        volunteerinitial: volunteerDetails?.initials || "",
        transfertype: "",
        targetstudyid: "",
        rescreeningrequired: "No",
        transferreason: "",
        remarks: "",
      } as any);
    }
  }, [editId, transferRecord, reset, selectedstudycode, volunteerDetails]);

  useEffect(() => {
    const load = async () => {
      const result = await loadCodelistsDetailId([
        "VOLUNTEER_TRANSFER_TYPE_OPTIONS",
        "VOLUNTEER_TRANSFER_REASON_OPTIONS"
      ]);
      const allLoaded = Object.values(result).every((flag) => flag);
      if (!allLoaded) {
        showToastMessage("error", "Some dropdowns failed to load");
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (volunteerDetails?.initials) {
      setValue("volunteerinitial", volunteerDetails.initials);
    }
  }, [volunteerDetails, setValue]);

  // -------------------------------------------------------------------------------------------
  // 7. Handlers & Actions
  // -------------------------------------------------------------------------------------------
  const MAX_TOTAL_SIZE = 20 * 1024 * 1024; // 20 MB limit

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const currentTotal = uploadedFiles.reduce((sum, f) => sum + f.size, 0);
    const incomingTotal = files.reduce((sum, f) => sum + f.size, 0);
    if (currentTotal + incomingTotal > MAX_TOTAL_SIZE) {
      showToastMessage("error","Total attachment size cannot exceed 20 MB.");
      e.target.value = "";
      return;
    }
    setUploadedFiles((prev) => [...prev, ...files]);
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    const currentTotal = uploadedFiles.reduce((sum, f) => sum + f.size, 0);
    const incomingTotal = files.reduce((sum, f) => sum + f.size, 0);
    if (currentTotal + incomingTotal > MAX_TOTAL_SIZE) {
      showToastMessage("error","Total attachment size cannot exceed 20 MB.");
      return;
    }
    setUploadedFiles((prev) => [...prev, ...files]);
  };

  const removeFile = (index: number) =>
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));

  const handleDeleteExisting = (attachmentId: number) => {
    setDeletedAttachmentIds((prev) => [...prev, attachmentId]);
    setExistingAttachments((prev) =>
      prev.filter((a) => Number(a.transferattachmentid) !== Number(attachmentId))
    );
  };

  const handlePreview = (file: any) => {
      const API_BASE_URL =  sessionStorage.getItem("baseUrl") || process.env.REACT_APP_BASE_URL
      const baseUrl = API_BASE_URL?.replace("/api", "") || "";

    // NEW FILE (File object)
    if (file instanceof File) {
      setPreviewFile(file);
      setPreviewFileUrl(null);
      setPreviewOpen(true);
      return;
    }

    // EXISTING FILE (API object)
    const path = file?.filepath || file?.path;

    if (!path) {
      console.error("Invalid file object:", file);
      return;
    }

    const finalUrl = path.startsWith("http")
      ? path
      : `${baseUrl}${path.startsWith("/") ? path : "/" + path}`;

    setPreviewFile(null);
    setPreviewFileUrl(finalUrl);
    setPreviewOpen(true);
  };

  const btnReasonClick = (reasonPayload: { reason: string }) => {
    if (!pendingUpdatePayload || !editId) return;

    UpdateMutation.mutate(
      {
        ...pendingUpdatePayload,
        updatereason: reasonPayload.reason,
      },
      {
        onSuccess: (res: any) => {
          queryClient.invalidateQueries({ queryKey: ["transfer-volunteer-select-all"] });
          showToastMessage("success", res?.message || "Transfer record updated successfully");
          setIsReasonModal(false);
          setPendingUpdatePayload(null);
          const returnTo = location.state?.returnTo;
          if (returnTo === STUDY_PATHS.SCREENING_LIST) {
            navigate(STUDY_PATHS.SCREENING_LIST, { state: { activeTab: "transferred" } });
          } else if (returnTo) {
            navigate(returnTo);
          } else {
            navigate(TRANSFER_VOLUNTEER_PATHS.LIST);
          }
        },
        onError: (err: any) => {
          console.error("Update Error:", err);
          showToastMessage("error", err?.message || err || "Update failed");
        },
      }
    );
  };

  const onSubmit = (values: TransferVolunteerFormValues) => {
    const payload = {
      ...values,
      volunteerid: volunteerIdToFetch || selectedvolunteerid,
      studyid: editId && transferRecord ? Number(transferRecord.fromstudyid) : selectedstudyid,
      attachments: uploadedFiles,
    };
    if (editId) {
      const updatePayload = {
        ...payload,
        volunteertransferid: Number(editId),
        deletedAttachmentIds: deletedAttachmentIds,
      };
      setPendingUpdatePayload(updatePayload);
      setIsReasonModal(true);
    } else {
      InsertMutation.mutate(payload as any, {
        onSuccess: (response: any) => {
          queryClient.invalidateQueries({ queryKey: ["transfer-volunteer-select-all"] });
          queryClient.invalidateQueries({ queryKey: ["verification-notification-count"] });
          showToastMessage("success", response?.message || "Transfer Record Submitted successfully!");
          navigate(STUDY_PATHS.SCREENING_LIST, { state: { activeTab: "transferred" } });
        },
        onError: (err: any) => {
          console.error("Error", err);
          showToastMessage("error", err || "Failed to submit Transfer Record");
        },
      });
    }
  };

  const handleClear = () => {
    reset();
    setUploadedFiles([]);
  };

  if (TransferSelectQuery?.isFetching || VolunteerSelectQuery?.isFetching) {
    return (
      <FullPageLoader message="Fetching details..." />
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        {/* HEADER */}
        <div className="d-flex justify-content-between align-items-center mt-1 mb-3 px-2">
          <div>
            <div className="d-flex align-items-center gap-2 mb-1">
              <h5 className="mb-0 fw-bold text-dark">{editId ? "Edit" : "Add"} Transfer Record</h5>
            </div>
            <p className="text-muted fs-13 mb-0">
              Transfer volunteers between clinical studies or clinical sites for protocol-related, administrative, or scheduling requirements.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          {/* SUBJECT IDENTIFICATION */}
          <SectionCard
            icon="ri-user-search-line"
            title="Subject Identification"
            subtitle="Verify and confirm the subject details being transferred"
          >
            <CommonForm
              fields={getSubjectFields()}
              control={control}
              errors={errors}
              columns={3}
              setValue={setValue}
            />

            {volunteerDetails && (
              <div className={styles.subjectInfoStrip}>
                {profileurl ? (
                  <img src={profileurl} alt={volunteerDetails.initials} className={styles.subjectAvatar} />
                ) : (
                  <div className={styles.subjectAvatar} style={{ background: "#e8f0fe", color: "#3c6bc4", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "bold" }}>
                    {volunteerDetails.initials}
                  </div>
                )}
                <div className={styles.metaGrid}>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Age / Gender</span>
                    <span className={styles.metaValue}>
                      {volunteerDetails.age} / {volunteerDetails.gendertext}
                    </span>
                  </div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Blood Group</span>
                    <span className={styles.metaValue}>{volunteerDetails.bloodgrouptext || "—"}</span>
                  </div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Weight</span>
                    <span className={styles.metaValue}>{volunteerDetails.weight || "—"}</span>
                  </div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Height</span>
                    <span className={styles.metaValue}>{volunteerDetails.height || "—"}</span>
                  </div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>BMI</span>
                    <span className={styles.metaValue}>{volunteerDetails.bmi || "—"}</span>
                  </div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Status</span>
                    <span className={`${styles.metaValue} ${styles.statusActive}`}>{volunteerDetails.status}</span>
                  </div>
                </div>
              </div>
            )}
          </SectionCard>

          {/* TRANSFER DETAILS */}
          <SectionCard
            icon="ri-split-cells-horizontal"
            title="Transfer Details"
            subtitle="Specify the transfer type, target study, and dates"
          >
            <CommonForm
              fields={getTransferFields(studyOptions, VOLUNTEER_TRANSFER_TYPE_OPTIONS)}
              control={control}
              errors={errors}
              columns={2}
              setValue={setValue}
            />

            <div className="mt-3">
              <CommonForm
                fields={getReasonFields(VOLUNTEER_TRANSFER_REASON_OPTIONS)}
                control={control}
                errors={errors}
                columns={2}
                setValue={setValue}
              />
            </div>

            <div className="mt-3">
              <CommonForm
                fields={getEligibilityFields()}
                control={control}
                errors={errors}
                columns={1}
                setValue={setValue}
              />
            </div>
          </SectionCard>

          {/* SUPPORTING DOCUMENTS */}
          <SectionCard
            icon="ri-attachment-2"
            title="Supporting Documents"
            subtitle="Upload transfer approvals, protocol deviations, or other supporting documents"
          >
            {/* File Upload Zone */}
            <div className="mb-4">
              <CommonFileProgress
                files={uploadedFiles}
                existingSizeBytes={existingAttachments.reduce((sum, f) => sum + parseSizeToBytes(f.size), 0)}
                maxSizeMB={20}
              />
              <div
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                style={{
                  border: `1px dashed ${isDragging ? "#2E86AB" : "#d0d5dd"}`,
                  borderRadius: 8,
                  padding: "40px 20px",
                  textAlign: "center",
                  cursor: "pointer",
                  background: isDragging ? "#f0f7ff" : "#fafbfc",
                  transition: "all 0.2s",
                }}
              >
                <i
                  className="ri-upload-cloud-2-line text-primary"
                  style={{ fontSize: 32, display: "block", marginBottom: 8 }}
                />
                <p className="mb-1 fw-bold text-dark" style={{ fontSize: 14 }}>
                  Drag & drop files here, or click to browse
                </p>
                <p className="text-muted mb-3" style={{ fontSize: 12 }}>
                  Accepted: PDF, JPG, PNG, DOCX - Max 10MB per file
                </p>
                <Button
                  type="button"
                  color="light"
                  size="sm"
                  className="border px-3"
                  onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                >
                  Browse Files
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.docx"
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
              </div>

              {/* Existing attachments from server */}
              {existingAttachments.length > 0 && (
                <div className="mt-3 d-flex flex-column gap-2">
                  <p className="text-muted small fw-medium mb-1">Existing Attachments</p>
                  <Row className="gap-2 px-0 w-100 mx-auto">
                    {existingAttachments.map((file, idx) => (
                      <Col
                        key={`existing-${idx}`}
                        md={4}
                        sm={12}
                        className={`px-0 ${addVolunteerStyle.ipInventory_wrapper}`}
                      >
                        <div className={`${addVolunteerStyle.attachmentCard} h-100`}>
                          <div className={addVolunteerStyle.fileInfo}>
                            <div className={addVolunteerStyle.fileIcon}>
                              <i className="ri-file-pdf-line text-danger" />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div className={addVolunteerStyle.fileName}>
                                <TruncatedText text={file.filename} />
                              </div>
                              <div className={addVolunteerStyle.fileMeta}>
                                {file.size}
                              </div>
                            </div>
                          </div>
                          <div className={addVolunteerStyle.fileActions}>
                            <i
                              className="ri-eye-line text-primary"
                              title="Preview"
                              style={{ cursor: "pointer", marginRight: "10px" }}
                              onClick={() => handlePreview(file)}
                            />
                            <i
                              className="ri-delete-bin-line text-danger"
                              title="Delete"
                              style={{ cursor: "pointer" }}
                              onClick={() => handleDeleteExisting(file.transferattachmentid)}
                            />
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </div>
              )}

              {/* Newly uploaded files */}
              {uploadedFiles.length > 0 && (
                <div className="mt-3 d-flex flex-column gap-2">
                  <p className="text-muted small fw-medium mb-1">New Attachments</p>
                  <Row className="gap-2 px-0 w-100 mx-auto">
                    {uploadedFiles.map((file, idx) => (
                      <Col
                        key={idx}
                        md={4}
                        sm={12}
                        className={`px-0 ${addVolunteerStyle.ipInventory_wrapper}`}
                      >
                        <div className={`${addVolunteerStyle.attachmentCard} h-100`}>
                          <div className={addVolunteerStyle.fileInfo}>
                            <div className={addVolunteerStyle.fileIcon}>
                              <i className="ri-file-pdf-line text-danger" />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div className={addVolunteerStyle.fileName}>
                                <TruncatedText text={file.name} />
                              </div>
                              <div className={addVolunteerStyle.fileMeta}>
                                {(file.size / 1024).toFixed(2)} KB
                              </div>
                            </div>
                          </div>
                          <div className={addVolunteerStyle.fileActions}>
                            <i
                              className="ri-eye-line text-primary"
                              title="Preview"
                              onClick={(e) => { e.stopPropagation(); handlePreview(file); }}
                              style={{ cursor: "pointer", marginRight: "10px" }}
                            />
                            <i
                              className="ri-delete-bin-line text-danger"
                              title="Delete"
                              onClick={(e) => { e.stopPropagation(); removeFile(idx); }}
                              style={{ cursor: "pointer" }}
                            />
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </div>
              )}
            </div>
          </SectionCard>

          {/* Sticky Footer */}
          <div className={formStyles.stickyFooter}>
            <div className={formStyles.footerActions}>
              <Button type="button" color="dark" outline onClick={() => {
                handleClear();
                const returnTo = location.state?.returnTo;
                if (returnTo === STUDY_PATHS.SCREENING_LIST) {
                  navigate(STUDY_PATHS.SCREENING_LIST, { state: { activeTab: "transferred" } });
                } else if (returnTo) {
                  navigate(returnTo);
                } else {
                  navigate(TRANSFER_VOLUNTEER_PATHS.LIST);
                }
              }}>
                Cancel
              </Button>
              <div className="d-flex gap-2">
                <Button type="submit" color="primary" disabled={InsertMutation.isPending || UpdateMutation.isPending}>
                  {InsertMutation.isPending ? <Spinner size="sm" className="me-2" /> : <i className="ri-check-line me-1" />}
                  {editId ? "Update" : "Submit"} Transfer Record
                </Button>
              </div>
            </div>
          </div>
        </form>

        {/* File Preview Modal */}
        <FilePreviewModal
          file={previewFile}
          fileUrl={previewFileUrl}
          isOpen={previewOpen}
          onClose={() => setPreviewOpen(false)}
        />

        {isReasonModal && (
          <Reason
            isOpen={isReasonModal}
            toggle={() => setIsReasonModal(!isReasonModal)}
            action="Update"
            id={Number(editId)}
            click={btnReasonClick}
            isLoading={UpdateMutation.isPending}
          />
        )}
      </Container>
    </div>
  );
};

export default AddTransferVolunteer;
